import type { DriftAccount, DriftQueueFilter } from '@interfaces/driftQueue';
import type { DataTableTanstackTable } from '@ovComponents/2-component/dataTable';
import { useDataTable } from '@ovComponents/2-component/dataTable';
import { useEffect, useMemo } from 'react';
import { MOCK_DRIFT_ACCOUNTS } from './driftQueue.mocks';
import { useDriftQueueColumns } from './driftQueueColumns';

export type UseDriftQueueArgs = {
  activeFilter: DriftQueueFilter;
  /** Override source rows (e.g. Storybook empty state). Defaults to mock data. */
  accounts?: DriftAccount[];
};

export type UseDriftQueueCounts = {
  needsAction: number;
  singleBreach: number;
  compoundBreach: number;
};

export type UseDriftQueueReturn = {
  table: DataTableTanstackTable<DriftAccount>;
  counts: UseDriftQueueCounts;
  isLoading: boolean;
  error: Error | null;
  totalRowCount: number;
  filteredRowCount: number;
};

/**
 * Central hook for the Drift Queue page: loads data, derives counts,
 * applies the active filter, and returns a configured TanStack table instance.
 *
 * @remarks
 * Currently backed by `MOCK_DRIFT_ACCOUNTS`. Swap to
 * `useQuery(FETCH_DRIFT_QUEUE, ...)` without changing the returned shape
 * when the backend endpoint is available.
 */
export function useDriftQueue({
  activeFilter,
  accounts,
}: UseDriftQueueArgs): UseDriftQueueReturn {
  const columns = useDriftQueueColumns();

  // Source data — replace with useQuery when the backend is ready
  const allAccounts = accounts ?? MOCK_DRIFT_ACCOUNTS;

  const counts: UseDriftQueueCounts = useMemo(
    () => ({
      needsAction: allAccounts.filter((a) => a.breaches.length > 0).length,
      singleBreach: allAccounts.filter(
        (a) => a.breachType === 'single' && a.breaches.length > 0,
      ).length,
      compoundBreach: allAccounts.filter(
        (a) => a.breachType === 'compound' && a.breaches.length > 0,
      ).length,
    }),
    [allAccounts],
  );

  const filteredAccounts = useMemo(() => {
    switch (activeFilter) {
      case 'needsAction':
        return allAccounts.filter((a) => a.breaches.length > 0);
      case 'singleBreach':
        return allAccounts.filter(
          (a) => a.breachType === 'single' && a.breaches.length > 0,
        );
      case 'compoundBreach':
        return allAccounts.filter(
          (a) => a.breachType === 'compound' && a.breaches.length > 0,
        );
      default:
        return allAccounts;
    }
  }, [activeFilter, allAccounts]);

  const { table } = useDataTable<DriftAccount>({
    data: filteredAccounts,
    columns,
    sortingMode: 'client',
    paginationMode: 'client',
    rowsPerPage: 25,
  });

  useEffect(() => {
    table.setPageIndex(0);
  }, [activeFilter, table]);

  // `autoResetAll` is off on the underlying table — clamp when the filtered
  // dataset shrinks (e.g. injected `accounts` refresh) so pageIndex stays in range.
  useEffect(() => {
    const maxIndex = Math.max(0, table.getPageCount() - 1);
    const { pageIndex } = table.getState().pagination;
    if (pageIndex > maxIndex) {
      table.setPageIndex(maxIndex);
    }
  }, [filteredAccounts.length, table]);

  return {
    table,
    counts,
    isLoading: false,
    error: null,
    // `totalRowCount` is the denominator for the footer's "X of Y" copy. The
    // footer reads "needing action", so this is the count of accounts with at
    // least one breach across all filter buckets — not `allAccounts.length`,
    // which would also include rows that never appear under any filter.
    totalRowCount: counts.needsAction,
    filteredRowCount: filteredAccounts.length,
  };
}
